import { Badge, BadgeVariants } from "~/components/Badge";
import { uniqueValues } from "~/utils/functions/uniqueValues";
import { Tooltip } from "~/components/Tooltip";
import { cn } from "~/lib/utils";

// // // //

/**
 * BadgeArray
 * Renders an array of strings as a series of Badges
 */
export function BadgeArray(props: {
    values: string[] | null | undefined;
    limit?: number;
    size?: "sm" | "md" | "lg" | "xl";
    className?: string;
    orientation?: "horizontal" | "vertical";
    tooltipTitle?: string;
    icon?: React.ReactNode;
}) {
    const {
        values,
        limit = 3,
        size = "sm",
        className,
        orientation = "horizontal",
        tooltipTitle,
        icon,
    } = props;

    if (!Array.isArray(values)) {
        return null;
    }

    const allValues = values.filter((each) => each.trim() !== "");
    const items = uniqueValues(allValues);

    if (items.length === 0) {
        return null;
    }

    const wrapperClassName = cn("flex gap-1", className, {
        "flex-row": orientation === "horizontal",
        "flex-col": orientation === "vertical",
    });

    const badgeClassName = cn(
        "w-min whitespace-nowrap overflow-hidden max-w-full cursor-default",
        {
            "flex items-center gap-1": icon,
        },
    );

    if (items.length < limit) {
        return (
            <div className={wrapperClassName}>
                {items.map((ac) => {
                    return (
                        <Badge
                            key={ac}
                            size={size}
                            rounded="sm"
                            className={badgeClassName}
                            variant={BadgeVariants.gray}
                            label={
                                <>
                                    {icon && <>{icon}</>}
                                    <span className="dg-overflow">{ac}</span>
                                </>
                            }
                        />
                    );
                })}
            </div>
        );
    }

    return (
        <Tooltip
            placement="top"
            tooltipWrapperClassName="rounded-lg shadow-sm select-none border"
            tooltipContent={
                <div className="px-3 flex items-center gap-2">
                    <div className="py-1.5 text-foreground">
                        {tooltipTitle && (
                            <p className="text-sm text-muted-foreground font-normal tracking-wide uppercase mb-1">
                                {tooltipTitle}
                            </p>
                        )}
                        <ul>
                            {items.map((ac) => (
                                <li
                                    key={ac}
                                    className="whitespace-nowrap text-sm"
                                >
                                    {tooltipTitle && (
                                        <span className="mr-1">•</span>
                                    )}
                                    {ac}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            }
        >
            {({ setTriggerRef }) => (
                <div ref={setTriggerRef} className={wrapperClassName}>
                    {items.slice(0, limit).map((ac) => (
                        <Badge
                            key={ac}
                            size={size}
                            rounded="sm"
                            className={badgeClassName}
                            variant={BadgeVariants.gray}
                            label={
                                <>
                                    {icon && <>{icon}</>}
                                    <span className="dg-overflow">{ac}</span>
                                </>
                            }
                        />
                    ))}
                    <Badge
                        size={size}
                        rounded="sm"
                        className="w-min whitespace-nowrap cursor-default"
                        label={`+ ${items.length - limit} More`}
                        variant={BadgeVariants.blue}
                    />
                </div>
            )}
        </Tooltip>
    );
}
